import type { Instant } from '@hoedokloop/domain';

import type {
  AnalyticsConsentChoice,
  AnalyticsConsentState,
  AnalyticsTransport,
  CapabilityResult,
} from './ports';

export const ANALYTICS_CONSENT_STORAGE_KEY = 'hoedokloop:analytics-consent:v1';

export interface AnalyticsConsentStore {
  read(): string | null;
  write(choice: AnalyticsConsentChoice): void;
}

type ConsentGatedAnalyticsOptions = {
  delegate: Pick<AnalyticsTransport, 'send'>;
  store: AnalyticsConsentStore;
  now: () => Instant;
};

function success<Value>(value: Value, observedAt: Instant): CapabilityResult<Value> {
  return { ok: true, value, observedAt };
}

function failed<Value>(observedAt: Instant): CapabilityResult<Value> {
  return { ok: false, reason: 'failed', retryable: true, observedAt };
}

function readPersistedConsent(store: AnalyticsConsentStore): AnalyticsConsentState {
  try {
    const value = store.read();
    return value === 'granted' || value === 'denied' ? value : 'undecided';
  } catch {
    return 'undecided';
  }
}

/**
 * The single analytics policy owner shared by public web and Apps in Toss.
 *
 * Consent defaults to undecided and every send is blocked until a grant is
 * successfully written and read back. A denial takes effect in memory before
 * persistence is attempted, so a revocation blocks all subsequent sends even
 * when browser storage is temporarily unavailable.
 */
export function createConsentGatedAnalyticsTransport({
  delegate,
  store,
  now,
}: ConsentGatedAnalyticsOptions): AnalyticsTransport {
  let state = readPersistedConsent(store);
  const listeners = new Set<(next: AnalyticsConsentState) => void>();

  const publish = (next: AnalyticsConsentState) => {
    state = next;
    for (const listener of listeners) listener(next);
  };

  return {
    consent: () => state,
    setConsent: (choice) => {
      if (choice === 'denied') {
        publish('denied');
        try {
          store.write(choice);
          return success(undefined, now());
        } catch {
          return failed(now());
        }
      }

      try {
        store.write(choice);
        if (store.read() !== choice) {
          publish('undecided');
          return failed(now());
        }
        publish('granted');
        return success(undefined, now());
      } catch {
        publish('undecided');
        return failed(now());
      }
    },
    subscribeConsent: (listener) => {
      listeners.add(listener);
      return success(() => listeners.delete(listener), now());
    },
    send: async (events) => {
      if (events.length === 0 || state !== 'granted') {
        return success({ accepted: 0 }, now());
      }
      return delegate.send(events);
    },
  };
}
